import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { currentDate, getLast7DaysDateRange } from '../../utils/constants/formInitialStates';

const Datepicker = lazy(() => import('react-tailwindcss-datepicker'));

export const normalizeDateOnly = (value) => {
	if (!value) return '';
	if (typeof value === 'string') {
		const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
		return match ? match[1] : '';
	}
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		const year = value.getFullYear();
		const month = String(value.getMonth() + 1).padStart(2, '0');
		const day = String(value.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	return '';
};

export const toDatepickerDate = (value) => {
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? undefined : value;
	}

	const dateOnly = normalizeDateOnly(value);
	if (!dateOnly) return undefined;

	const [year, month, day] = dateOnly.split('-').map(Number);
	return new Date(year, month - 1, day);
};

export const clampDateRangeToMinDate = (range, minDate) => {
	const minimum = normalizeDateOnly(minDate);
	if (!range || !minimum) return range;

	const clamp = (value) => {
		const date = normalizeDateOnly(value);
		return date && date < minimum ? minimum : value;
	};

	return {
		...range,
		startDate: clamp(range.startDate),
		endDate: clamp(range.endDate),
	};
};

/**
 * Reusable date range picker that defaults to last 7 days (Canada/Pacific).
 * Use in any page; get start and end date via onChange({ startDate, endDate }).
 * Timezone is global via formInitialStates APP_TIMEZONE (Canada/Pacific).
 *
 * @param {Function} onChange - (payload) => void. payload = { startDate: string, endDate: string } (YYYY-MM-DD). Called on mount with default range and when user changes range.
 * @param {Object} value - Optional controlled value { startDate, endDate }. If not provided, component manages its own state.
 * @param {string|Date} minDate - Optional earliest selectable date.
 * @param {Object} maxDate - Optional max selectable date (default: today in Canada/Pacific).
 * @param {string} className - Optional wrapper class (e.g. "my-4 w-64").
 * @param {string} inputClassName - Optional input class.
 * @param {string} toggleClassName - Optional calendar icon button class.
 * @param {string} placeholder - Optional input placeholder.
 * @param {boolean} showShortcuts - Optional (default true).
 * @param {Array<string>} hiddenShortcuts - Optional array of shortcut keys to hide (e.g., ['last30days', 'thisMonth', 'lastMonth']).
 * @param {Object} customShortcuts - Optional object with custom shortcuts to add (e.g., { last3months: { text: 'Last 3 months', period: { start: Date, end: Date } } }).
 * @param {boolean} useRange - Optional (default true). false shows a single month, for narrow containers.
 * @param {boolean} fixedPopup - Optional (default false). Positions the calendar relative to the viewport,
 *   right-aligned under the input, so it is not clipped inside scrolling/overflow-hidden containers.
 */
const Last7DaysDateRangePicker = ({
	onChange,
	value: controlledValue,
	minDate,
	maxDate,
	className = 'my-4 w-64',
	inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white',
	toggleClassName,
	showShortcuts = true,
	enableFutureDates = false,
	hiddenShortcuts = [],
	customShortcuts = {},
	useRange = true,
	fixedPopup = false,
	placeholder,

}) => {
	const wrapperRef = useRef(null);
	const [popupPosition, setPopupPosition] = useState(null);

	// Keep the fixed popup anchored under the input while the page or a parent column scrolls.
	useEffect(() => {
		if (!fixedPopup) return undefined;

		const updatePosition = () => {
			const input = wrapperRef.current?.querySelector('input');
			if (!input) return;
			const rect = input.getBoundingClientRect();
			setPopupPosition({ top: rect.bottom, right: Math.max(8, window.innerWidth - rect.right) });
		};

		updatePosition();
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);
		return () => {
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [fixedPopup]);

	const defaultRange = getLast7DaysDateRange(enableFutureDates);
	const defaultValue = clampDateRangeToMinDate({
		startDate: defaultRange.startDate,
		endDate: defaultRange.endDate,
	}, minDate);

	const [internalValue, setInternalValue] = useState(defaultValue);
	const value = controlledValue !== undefined && controlledValue !== null ? controlledValue : internalValue;

	// Notify parent of default range on mount (uncontrolled only) so they get startDate and endDate immediately
	useEffect(() => {
		if (controlledValue === undefined && onChange && defaultValue.startDate && defaultValue.endDate) {
			onChange(defaultValue);
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps -- announce the uncontrolled default once.

	const handleChange = (newValue) => {
		const safeValue = clampDateRangeToMinDate(
			newValue || { startDate: null, endDate: null },
			minDate
		);
		if (controlledValue === undefined) {
			setInternalValue(safeValue);
		}
		if (onChange) {
			onChange(safeValue);
		}
	};

	// maxDate: use end-of-day for currentDate (Canada/Pacific) so "today" in CA is selectable.
	// new Date(currentDate) is UTC midnight, so in Pacific "today" can appear disabled; use end-of-day UTC.
	const getMaxDateForTodayCA = () => {
		// If enableFutureDates is true, don't restrict dates (allow all future dates)
		if (enableFutureDates) {
			// If maxDate is explicitly provided, use it; otherwise allow all future dates
			return maxDate != null ? toDatepickerDate(maxDate) : undefined;
		}
		// If enableFutureDates is false, restrict to today or provided maxDate
		if (maxDate != null) return toDatepickerDate(maxDate);
		const [y, m, d] = currentDate.split('-').map(Number);
		return new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999));
	};
	const minDateResolved = toDatepickerDate(minDate);
	const maxDateResolved = getMaxDateForTodayCA();

	// Build custom shortcuts config if hiddenShortcuts or customShortcuts are provided
	const getShortcutsConfig = () => {
		if (!showShortcuts) {
			return undefined;
		}

		// If no hiddenShortcuts and no customShortcuts, use default shortcuts
		if (!minDate && hiddenShortcuts.length === 0 && Object.keys(customShortcuts).length === 0) {
			return undefined; // Use default shortcuts
		}

		const now = new Date();
		const shortcuts = {};

		// Add shortcuts that are not hidden
		if (!hiddenShortcuts.includes('today')) {
			shortcuts.today = {
				text: 'Today',
				period: { start: now, end: now }
			};
		}

		if (!hiddenShortcuts.includes('yesterday')) {
			shortcuts.yesterday = {
				text: 'Yesterday',
				period: { start: new Date(now.getTime() - 86400000), end: new Date(now.getTime() - 86400000) }
			};
		}

		if (!hiddenShortcuts.includes('last7days')) {
			shortcuts.last7days = {
				text: 'Last 7 days',
				period: { start: new Date(now.getTime() - 6 * 86400000), end: now }
			};
		}

		if (!hiddenShortcuts.includes('last30days')) {
			shortcuts.last30days = {
				text: 'Last 30 days',
				period: { start: new Date(now.getTime() - 29 * 86400000), end: now }
			};
		}

		if (!hiddenShortcuts.includes('thisMonth')) {
			shortcuts.thisMonth = {
				text: 'This month',
				period: {
					start: new Date(now.getFullYear(), now.getMonth(), 1),
					end: now
				}
			};
		}

		if (!hiddenShortcuts.includes('lastMonth')) {
			shortcuts.lastMonth = {
				text: 'Last month',
				period: {
					start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
					end: new Date(now.getFullYear(), now.getMonth(), 0)
				}
			};
		}

		// Merge custom shortcuts
		Object.assign(shortcuts, customShortcuts);

		const minimum = normalizeDateOnly(minDate);
		const allowedShortcuts = minimum
			? Object.fromEntries(Object.entries(shortcuts).filter(([, shortcut]) => {
				const shortcutStart = normalizeDateOnly(shortcut?.period?.start);
				return shortcutStart && shortcutStart >= minimum;
			}))
			: shortcuts;

		return { shortcuts: allowedShortcuts };
	};

	const shortcutsConfig = getShortcutsConfig();

	// Same classes as the library default, but `fixed` and placed from CSS variables set on the wrapper.
	// The library toggles hidden/block, opacity and translate classes itself, so those must stay.
	const fixedPopupClassName = fixedPopup
		? 'transition-all ease-out duration-300 fixed z-50 mt-[1px] text-sm lg:text-xs 2xl:text-sm translate-y-4 opacity-0 hidden !left-auto !top-[var(--dp-popup-top)] !right-[var(--dp-popup-right)]'
		: undefined;
	const wrapperStyle = fixedPopup && popupPosition
		? { '--dp-popup-top': `${popupPosition.top}px`, '--dp-popup-right': `${popupPosition.right}px` }
		: undefined;

	return (
		<div
			ref={wrapperRef}
			className={className}
			style={wrapperStyle}
			onMouseDownCapture={fixedPopup ? () => {
				const input = wrapperRef.current?.querySelector('input');
				if (!input) return;
				const rect = input.getBoundingClientRect();
				setPopupPosition({ top: rect.bottom, right: Math.max(8, window.innerWidth - rect.right) });
			} : undefined}
		>
			<Suspense fallback={<div className="h-[38px] rounded-lg border border-gray-300 bg-gray-50 animate-pulse" aria-hidden />}>
				<Datepicker
					value={value}
					onChange={handleChange}
					showShortcuts={showShortcuts}
					useRange={useRange}
					popupClassName={fixedPopupClassName}
					primaryColor="blue"
					minDate={minDateResolved}
					maxDate={maxDateResolved}
					inputClassName={inputClassName}
					toggleClassName={toggleClassName}
					placeholder={placeholder}
					popoverDirection="down"
					configs={shortcutsConfig}
				/>
			</Suspense>
		</div>
	);
};

Last7DaysDateRangePicker.displayName = 'Last7DaysDateRangePicker';

export default Last7DaysDateRangePicker;
